import { useState } from 'react'
import {
  deleteProduct,
  insertProduct,
  productChanged,
  updateProduct,
  type product,
} from '../../../utils/productUtils'
import { getMessage } from '../../../utils/messageUtils'

import NumericInput from '../../PopupForm/NumericInput'
import PopupError from '../../PopupForm/PopupError'
import type { User } from '../../../utils/loginUtils'
import Popup, { type ExtraButton } from '../../PopupForm/Popup'
import Spinner from '../../Spinner/Spinner'

import './ProductPopUpContent.scss'

interface ProductPopUpContentProps {
  product: product | null
  currentShops: string[]
  productList: product[] | undefined
  user: User
  isMobile: boolean
  setPopupOnOk: React.Dispatch<React.SetStateAction<(() => void) | undefined>>
  closePopup: () => void
  reLoadData: () => void
  setPopupExtraButtons: React.Dispatch<
    React.SetStateAction<ExtraButton[] | undefined>
  >
}

function ProductPopUpContent({
  product,
  currentShops,
  productList,
  user,
  isMobile,
  setPopupOnOk,
  closePopup,
  reLoadData,
  setPopupExtraButtons,
}: ProductPopUpContentProps) {
  const [name, setName] = useState(product?.name || '')
  const [shop, setShop] = useState(product?.shop || '')
  const [ingredient, setIngredient] = useState(
    product?.ingredient === 1 || false
  )
  const [storable, setStorable] = useState(product?.storable === 1 || false)
  const [pack, setPack] = useState(product?.pack || 1)

  const [errorMessage, setErrorMessage] = useState('')

  // Popup
  const [deletePopupOpen, setDeletePopupOpen] = useState(false)

  const [isLoading, setIsLoading] = useState(false)

  const checkProductInputs = () => {
    if (
      productList?.find(
        p => p.id !== product?.id && p.name.toLowerCase() === name.toLowerCase()
      )
    ) {
      setErrorMessage(getMessage('product_exists'))
      return false
    }
    if (name.trim() === '') {
      setErrorMessage(getMessage('product_name_empty'))
      return false
    }
    if (shop.trim() === '') {
      setErrorMessage(getMessage('product_shop_empty'))
      return false
    }
    if (pack < 1) {
      setErrorMessage(getMessage('product_pack_invalid'))
      return false
    }
    return true
  }

  const saveNewProduct = async () => {
    // Create the new recipe and send it to backend
    const newProduct: product = {
      name: name.trim(),
      shop: shop.trim(),
      ingredient: ingredient ? 1 : 0,
      storable: storable ? 1 : 0,
      pack,
    }
    return (await insertProduct(user.id!, newProduct)) as {
      success: boolean
      error?: string
    }
  }

  const updateExistingProduct = async () => {
    const newProduct: product = { ...product! }
    newProduct.name = name.trim()
    newProduct.shop = shop.trim()
    newProduct.ingredient = ingredient ? 1 : 0
    newProduct.storable = storable ? 1 : 0
    newProduct.pack = pack

    if (productChanged(product!, newProduct)) {
      return (await updateProduct(user.id!, newProduct)) as {
        success: boolean
        error?: string
      }
    } else {
      return undefined
    }
  }

  const clickDeleteProduct = () => {
    setDeletePopupOpen(true)
  }

  const deleteExistingProduct = async (productToDelete: product) => {
    setIsLoading(true)
    try {
      const result = (await deleteProduct(user.id!, productToDelete.id!)) as {
        success: boolean
        error?: string
      }
      if (!result.success) {
        setErrorMessage(
          getMessage('product_delete_error') + (result.error || '')
        )
      } else {
        reLoadData()
        closePopup()
      }
    } catch (error) {
      setErrorMessage(
        getMessage('product_delete_error') +
          (error instanceof Error ? error.message : '')
      )
    } finally {
      setIsLoading(false)
      setDeletePopupOpen(false)
    }
  }

  setPopupExtraButtons([
    {
      label: getMessage('delete'),
      className: 'popup-delete-button',
      onClick: product ? () => clickDeleteProduct() : undefined,
    },
  ] as ExtraButton[])

  const saveProduct = async () => {
    //Checks
    if (!checkProductInputs()) return

    setIsLoading(true)
    let result: { success: boolean; error?: string } | undefined
    if (!product) {
      result = await saveNewProduct()
    } else {
      result = await updateExistingProduct()
    }
    setIsLoading(false)
    if (result === undefined) {
      // No changes
      closePopup()
      return
    }
    if (!result.success) {
      setErrorMessage(getMessage('product_save_error') + (result.error || ''))
    } else {
      reLoadData()
      closePopup()
    }
  }
  setPopupOnOk(() => saveProduct)

  const labelClass = `pop-up-form-label ${isMobile ? 'pop-up-form-label-mobile' : ''}`
  const fieldClass = `pop-up-form-field ${isMobile ? 'pop-up-form-field-mobile' : ''}`
  return (
    <div className={`pop-up-form ${isMobile ? 'pop-up-form-mobile' : ''}`}>
      <PopupError
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
      <div className="pop-up-form-row">
        <span className={labelClass}>{getMessage('name')}:</span>
        <input
          className={fieldClass}
          type="text"
          placeholder={getMessage('product_name')}
          value={name}
          onChange={e => setName(e.target.value)}
        />
      </div>
      <div className="pop-up-form-row">
        <span className={labelClass}>{getMessage('shop')}:</span>
        <input
          className={fieldClass}
          type="text"
          placeholder={getMessage('product_shop')}
          value={shop}
          onChange={e => setShop(e.target.value)}
          list="product-shops"
        />
        <datalist id="product-shops">
          {currentShops.map((shopOption, index) => (
            <option key={index} value={shopOption} />
          ))}
        </datalist>
      </div>
      <NumericInput
        className={labelClass}
        label={getMessage('product_pack')}
        value={pack}
        setValue={setPack}
        isMobile={isMobile}
      />
      <div className="pop-up-form-checks">
        <div
          className={`pop-up-form-row pop-up-form-checks-row ${isMobile ? 'pop-up-form-checks-row-mobile' : ''}`}
        >
          <label className={labelClass}>
            <input
              className="pop-up-form-checkbox"
              type="checkbox"
              checked={ingredient}
              onChange={e => setIngredient(e.target.checked)}
            />{' '}
            {getMessage('product_ingredient_tick')}
          </label>
          <label className={labelClass}>
            <input
              className="pop-up-form-checkbox"
              type="checkbox"
              checked={storable}
              onChange={e => setStorable(e.target.checked)}
            />{' '}
            {getMessage('product_storable_tick')}
          </label>
        </div>
      </div>
      <Popup
        isOpen={deletePopupOpen}
        isMobile={isMobile}
        message={getMessage('product_delete_confirm')}
        onOk={() => deleteExistingProduct(product!)}
        onClose={() => setDeletePopupOpen(false)}
      />
      <Spinner isOpen={isLoading} overlay={true} />
    </div>
  )
}

export default ProductPopUpContent
